import { LogLevel } from "../types/api_types";
import { LogLevelEnum } from "../constants/enums";
import { IEventService, ScormEventCallback } from "../interfaces/services";

/**
 * Interface for a listener object
 */
interface Listener {
  functionName: string;
  CMIElement: string | null;
  callback: ScormEventCallback;
}

/**
 * Type for parsed listener information
 */
interface ParsedListener {
  functionName: string;
  CMIElement: string | null;
}

/**
 * Service for handling event listeners and event processing
 */
export class EventService implements IEventService {
  // Map of function names to listeners for faster lookups
  private listenerMap: Map<string, Listener[]> = new Map();
  // Total count of listeners for logging
  private listenerCount = 0;
  // Function to log API messages
  private readonly apiLog: (
    functionName: string,
    message: string,
    messageLevel: LogLevel,
    CMIElement?: string,
  ) => void;

  /**
   * Constructor for EventService
   * @param {Function} apiLog - Function to log API messages
   */
  constructor(
    apiLog: (
      functionName: string,
      message: string,
      messageLevel: LogLevel,
      CMIElement?: string,
    ) => void,
  ) {
    this.apiLog = apiLog;
  }

  /**
   * Parses a listener name into its components
   *
   * @param {string} listenerName - The name of the listener
   * @returns {ParsedListener|null} - The parsed listener information or null if invalid
   */
  private parseListenerName(listenerName: string): ParsedListener | null {
    // SVC-EVT-01: Handle empty string input
    if (!listenerName) return null;

    const listenerSplit = listenerName.split(".");

    const functionName = listenerSplit[0];
    let CMIElement: string | null = null;

    if (listenerSplit.length > 1) {
      CMIElement = listenerName.replace(`${functionName}.`, "");
    }

    return { functionName: functionName ?? listenerName, CMIElement };
  }

  /**
   * Provides a mechanism for attaching to a specific SCORM event
   *
   * @param {string} listenerName - The name of the listener
   * @param {Function} callback - The callback function to execute when the event occurs
   */
  on(listenerName: string, callback: ScormEventCallback) {
    if (!callback) return;

    const listenerFunctions = listenerName.split(" ");
    for (const listenerFunction of listenerFunctions) {
      const parsedListener = this.parseListenerName(listenerFunction);
      if (!parsedListener) continue;

      const { functionName, CMIElement } = parsedListener;

      // Get or create the array for this function name
      const listeners = this.listenerMap.get(functionName) ?? [];

      // Add the new listener
      listeners.push({
        functionName,
        CMIElement,
        callback,
      });

      // Update the map and count
      this.listenerMap.set(functionName, listeners);
      this.listenerCount++;

      this.apiLog(
        "on",
        `Added event listener: ${this.listenerCount}`,
        LogLevelEnum.INFO,
        functionName,
      );
    }
  }

  /**
   * Provides a mechanism for detaching a specific SCORM event listener
   *
   * @param {string} listenerName - The name of the listener to remove
   * @param {Function} callback - The callback function to remove
   */
  off(listenerName: string, callback: ScormEventCallback) {
    if (!callback) return;

    const listenerFunctions = listenerName.split(" ");
    for (const listenerFunction of listenerFunctions) {
      const parsedListener = this.parseListenerName(listenerFunction);
      if (!parsedListener) continue;

      const { functionName, CMIElement } = parsedListener;

      // Get the listeners for this function name
      const listeners = this.listenerMap.get(functionName);
      if (!listeners) continue;

      // Find the index of the listener to remove
      const removeIndex = listeners.findIndex(
        (obj) => obj.CMIElement === CMIElement && obj.callback === callback,
      );

      if (removeIndex !== -1) {
        // Remove the listener (modifies array in-place)
        listeners.splice(removeIndex, 1);
        this.listenerCount--;

        // Remove the map entry if no listeners remain
        // Note: No need to update the map otherwise, as we modified the array by reference
        if (listeners.length === 0) {
          this.listenerMap.delete(functionName);
        }

        this.apiLog(
          "off",
          `Removed event listener: ${this.listenerCount}`,
          LogLevelEnum.INFO,
          functionName,
        );
      }
    }
  }

  /**
   * Provides a mechanism for clearing all listeners from a specific SCORM event
   *
   * Note: clear() differs from off() in CMIElement matching behavior:
   * - clear() with CMIElement=null removes ALL listeners for the function
   * - off() requires exact CMIElement match AND callback match
   * This allows clear() to remove all listeners at once, while off() is surgical.
   *
   * @param {string} listenerName - The name of the listener to clear
   */
  clear(listenerName: string) {
    const listenerFunctions = listenerName.split(" ");
    for (const listenerFunction of listenerFunctions) {
      const parsedListener = this.parseListenerName(listenerFunction);
      if (!parsedListener) continue;

      const { functionName, CMIElement } = parsedListener;

      // If we have listeners for this function name
      if (this.listenerMap.has(functionName)) {
        const listeners = this.listenerMap.get(functionName)!;

        // When CMIElement is null (no specific element provided), clear ALL listeners for this function.
        // When CMIElement is specified, only clear listeners matching that exact CMIElement.
        const newListeners =
          CMIElement === null ? [] : listeners.filter((obj) => obj.CMIElement !== CMIElement);

        // Update the count and map
        this.listenerCount -= listeners.length - newListeners.length;

        if (newListeners.length === 0) {
          this.listenerMap.delete(functionName);
        } else {
          this.listenerMap.set(functionName, newListeners);
        }
      }
    }
  }

  /**
   * Processes any 'on' listeners that have been created
   *
   * @param {string} functionName - The name of the function that triggered the event
   * @param {string} CMIElement - The CMI element that was affected
   * @param {any} value - The value that was set
   */
  processListeners(functionName: string, CMIElement?: string, value?: any) {
    this.apiLog(functionName, value, LogLevelEnum.INFO, CMIElement);

    // Get listeners for this function name
    const listeners = this.listenerMap.get(functionName);
    if (!listeners) return;

    for (const listener of listeners) {
      const listenerHasCMIElement = !!listener.CMIElement;
      let CMIElementsMatch = false;

      // Match CMI elements: supports wildcard (*) for prefix matching or exact string comparison.
      // Listeners with no CMIElement (null) match all events for this function.
      if (CMIElement && listener.CMIElement) {
        if (listener.CMIElement.endsWith("*")) {
          // For wildcard matches, check if the CMI element starts with the prefix
          const prefix = listener.CMIElement.slice(0, -1);
          CMIElementsMatch = CMIElement.startsWith(prefix);
        } else {
          // For exact matches, compare the strings directly
          CMIElementsMatch = listener.CMIElement === CMIElement;
        }
      }

      // If the listener matches, call the callback
      if (!listenerHasCMIElement || CMIElementsMatch) {
        this.apiLog(
          "processListeners",
          `Processing listener: ${listener.functionName}`,
          LogLevelEnum.DEBUG,
          CMIElement,
        );

        // Dispatch to callback with appropriate arguments based on event type
        // Different event types have different callback signatures:
        // - Sequence events: callback(target)
        // - CommitError: callback(errorCode)
        // - CommitSuccess: callback()
        // - Regular events: callback(CMIElement, value)
        if (functionName.startsWith("Sequence")) {
          // For sequence events, pass the target as the first argument
          listener.callback(value);
        } else if (functionName === "CommitError") {
          // For commit error events, pass the error code
          listener.callback(value);
        } else if (functionName === "CommitSuccess") {
          // For commit success events, pass no arguments
          listener.callback();
        } else {
          // For regular events, pass CMIElement and value
          listener.callback(CMIElement, value);
        }
      }
    }
  }

  /**
   * Resets the event service by clearing all listeners
   */
  reset() {
    this.listenerMap.clear();
    this.listenerCount = 0;
  }
}
